import { Suspense, lazy, ElementType, useEffect } from 'react';
import { Navigate, useRoutes, useLocation, Outlet } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import ProgramsLayout from 'src/layouts/ProgramsLayout';
// pages
import Report from 'src/pages/reporting/Report';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from '../components/LoadingScreen';
import Plans from "../pages/saasSubscribe/PlansPage"; 
import { TransactionProvider } from 'src/contexts/TransactionContext';
import MerchantStatsLayout from 'src/layouts/MerchantStatsLayout';
import { CustomerDataType, TransactionOwnershipType } from 'src/@types/transactions';
import Scrollbar from 'src/components/Scrollbar';
import { CalculatorMode } from 'src/@types/emission';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/') && isAuthenticated;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { pathname } = useLocation();

  const delay = (ms: number) => new Promise(
      resolve => setTimeout(resolve, ms)
  );

  useEffect(() => {
    async function sendPageViewWithDelaySoThatPageTitleUpdated() {
      await delay(2000);
      // @ts-ignore
      window._hsq?.push(['setPath', pathname]);
      // @ts-ignore
      window._hsq?.push(['trackPageView']);
    }
    sendPageViewWithDelaySoThatPageTitleUpdated();

  }, [pathname]);

  const routesHelper = (pathname: string) => {
    if (pathname.includes('receipt') || pathname.includes('certificate') || pathname.includes('report')) {
      return <Outlet />;
    } else {
      return (
        <Scrollbar>
          <DashboardLayout />
        </Scrollbar>
      )
    }
  }

  return useRoutes([
    {
      path: '/auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: '/',
      element: (
        <AuthGuard>
          {routesHelper(pathname)}
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={'/dashboard'} replace />, index: true },
        { path: 'dashboard', element: <GeneralApp /> },
        {
          path: 'insights/:dateFrom/:dateTo/report',
          element: <Report />,
        },
        {
          path: 'programs/program/:type/:code/:dateFrom/:dateTo/report',
          element: <Report ownershipTypes={[TransactionOwnershipType.CUSTOMER]} />,
        },

        {
          path: 'emissions',
          children: [
            { element: <Navigate to="/emissions" replace />, index: true },
            { path: 'business-calculator', element: <BusinessCalculator mode={CalculatorMode.BUSINESS}/> },
            { path: 'business-calculator/:footprintId', element: <BusinessCalculator mode={CalculatorMode.BUSINESS}/> },
            { path: 'business-calculator/:footprintId/edit', element: <BusinessCalculator isEditPage={true} mode={CalculatorMode.BUSINESS}/> },
            { path: 'event-calculator', element: <BusinessCalculator mode={CalculatorMode.GENERIC_EVENT} /> },
            { path: 'event-calculator/:footprintId', element: <BusinessCalculator mode={CalculatorMode.GENERIC_EVENT} /> },
            { path: 'event-calculator/:footprintId/edit', element: <BusinessCalculator isEditPage={true} mode={CalculatorMode.GENERIC_EVENT} /> },
            { path: 'emission-records', element: <EmissionRecords /> },
          ],
        },
        {
          path: 'credits',
          children: [
            { element: <Navigate to="/credits" replace />, index: true },
            { path: 'purchase', element: <Purchase /> },

            // { path: 'view-credits/business-transactions', element: <Transactions /> },
            {
              path: 'purchase-history',
              element: (
                  <TransactionProvider>
                    <PurchaseHistory />
                  </TransactionProvider>
              ),
            },
            { path: 'transaction/:id', element: <Transaction /> },
            { path: 'transactions/:id/receipt', element: <EmailReceipt /> },
            { path: 'transactions/:id/certificate', element: <Certificate /> },

            { path: 'projects', element: <Projects /> },
            { path: 'project/:projectCode', element: <ProjectPage /> },

            { path: 'portfolios', element: <Baskets /> },
            { path: 'portfolio/:basketId', element: <BasketPage /> },
          ]
        },
        {
          path: 'insights',
          element: (
            <TransactionProvider>
              <MerchantStatsLayout currentPath='Impact Insights' />
            </TransactionProvider>
          ),
          children: [
            { element: <Navigate to="/insights" replace />, index: true },
            { path: 'impact', element: <MerchantImpact /> },
            { path: 'projects', element: <MerchantProjects /> },
            {
              path: 'transactions',
              element: (
                <Transactions
                  transactionTypes={[
                    TransactionOwnershipType.CUSTOMER,
                    TransactionOwnershipType.MERCHANT,
                  ]}
                />
              ),
            },
          ],
        },
        {
          path: 'credits',
          children: [
            { element: <Navigate to="/credits" replace />, index: true },
            { path: 'purchase-credits', element: <Cart /> },
          ]
        },
        {
          path: 'programs',
          children: [
            { element: <Navigate to="/programs" replace />, index: true },
            { path: 'toolkit', element: <ToolkitPage /> },
            { path: 'toolkit/:code', element: <ToolkitItemPage /> },
          ],
        },
        {
          path: 'programs',
          element: (
              <TransactionProvider>
                <ProgramsLayout />
              </TransactionProvider>
          ),
          children: [
            { element: <Navigate to="/programs" replace />, index: true },

            {
              path: 'program/:type/:code/transactions',
              element: <Transactions transactionTypes={[TransactionOwnershipType.CUSTOMER]} />,
            },
            { path: 'program/transaction/:id', element: <Transaction /> },
            { path: 'program/:type/:code/employees', element: <Customers type={CustomerDataType.EMPLOYEE} /> },
            { path: 'program/:type/:code/customers', element: <Customers type={CustomerDataType.CUSTOMER} /> },
            { path: 'program/:type/:code/impact', element: <Impact ownershipTypes={[TransactionOwnershipType.CUSTOMER]} /> },
            { path: 'program/:type/:code/engagement', element: <Engagement /> },
          ],
        },
        {
          path: 'programs',
          element: (
              <TransactionProvider>
                <ProgramsLayout currentPath='Contributions' />
              </TransactionProvider>
          ),
          children: [
            { element: <Navigate to="/programs" replace />, index: true },
            {
              path: 'collective-credits',
              element: <Transactions transactionTypes={[TransactionOwnershipType.CUSTOMER]} />
            },
          ],
        },
        {
          path: 'impact-stories',
          children: [
            { element: <Navigate to="/impact-stories" replace />, index: true },
            { path: 'tools', element: <ImpactStoriesPage /> },
            { path: 'tools/:code', element: <ToolkitItemPage /> },

            { path: 'pages', element: <SustainabilityPages /> },
            { path: 'pages/create', element: <TlpSettingsPage /> },
            { path: 'pages/update', element: <TlpSettingsPage /> },
          ],
        },
        {
          path: 'account',
          children: [
            { element: <Navigate to="/account" replace />, index: true },
            { path: 'plans', element: <Plans /> },
            { path: 'account-details', element: <UserProfile /> },
            { path: 'user-management', element: <UserManagement /> },
            { path: '/account/plans/confirmation', element: <PlanConfirmation /> },
            { path: '/account/plans/confirmation/cancel', element: <PlanConfirmation isCancel={true} /> },
          ],
        },
        {
          path: 'settings',
          children: [
            { element: <Navigate to="/settings" replace />, index: true },
            { path: 'theme', element: <ThemeSettingsPage /> },
          ],
        },
        { path: 'permission-denied', element: <PermissionDenied /> },
        { path: '500', element: <Page500 /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <Navigate to="/auth/register" replace />,
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));

// USER
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const PlanConfirmation = Loadable(lazy(() => import('../pages/saasSubscribe/PlanConfirmation')));
const UserManagement = Loadable(lazy(() => import('../pages/dashboard/UserManagement')))

// SETTINGS
const ThemeSettingsPage = Loadable(lazy(() => import('../pages/settings/ThemeSettingsPage')));

// OFFSET
const Transactions = Loadable(lazy(() => import('../pages/Transactions/Transactions')));
const PurchaseHistory = Loadable(lazy(() => import('../pages/Transactions/PurchaseHistory')));
const Transaction = Loadable(lazy(() => import('../pages/Transactions/Transaction')));
const Purchase = Loadable(lazy(() => import('../pages/purchase/PurchaseOneOff')));
const BusinessCalculator = Loadable(lazy(() => import('../pages/business-calculator/BusinessCalculator')));
const EmissionRecords = Loadable(lazy(() => import('../pages/emissions/EmissionRecords')));

const EmailReceipt = Loadable(lazy(() => import('../pages/documents/receipt/EmailReceipt')));
const Certificate = Loadable(lazy(() => import('../pages/documents/certificate')));
const Customers = Loadable(lazy(() => import('../pages/reporting/employee/Customers')));

const Engagement = Loadable(lazy(() => import('../pages/reporting/Engagement')));
const Impact = Loadable(lazy(() => import('../pages/Impact')));
const MerchantImpact = Loadable(lazy(() => import('../pages/reporting/merchant/Impact')));
const MerchantProjects = Loadable(lazy(() => import('../pages/reporting/merchant/Projects')));

const Cart = Loadable(lazy(() => import('../pages/purchase/CartPage')));

// ENGAGE
const ToolkitPage = Loadable(lazy(() => import('../pages/engage/ToolkitPage')));
const ToolkitItemPage = Loadable(lazy(() => import('../pages/engage/ToolkitItemPage')));
const SustainabilityPages = Loadable(lazy(() => import('../pages/engage/sustainabilityPages')));
const TlpSettingsPage = Loadable(lazy(() => import('../pages/engage/sustainabilityPages/configuration')));

// EXPLORE
const Baskets = Loadable(lazy(() => import('../pages/baskets/BasketsPage')));
const BasketPage = Loadable(lazy(() => import('../pages/baskets/BasketDetailsPage')));
const Projects = Loadable(lazy(() => import('../pages/projects/ProjectsPage')));
const ProjectPage = Loadable(lazy(() => import('../pages/projects/ProjectDetailsPage')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// MAIN
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));

const ImpactStoriesPage = Loadable(lazy(() => import('../pages/engage/ImpactStoriesPage')));
